/**
 * TabsElement — F12.H T12.H.4.
 *
 * Zakładki — element interaktywny z N zakładkami. Każda zakładka ma
 * swoje children (dzieci layoutu). User klika nagłówek → przełącza
 * widoczność.
 *
 * **Uwaga arch.:** w pierwszej iteracji (T12.H.4) zakładki są RENDERED
 * z wspólnymi children (przez `props.children`). Per-tab children
 * (każda zakładka osobny drzewo) wymaga zmiany w rendererze formularza
 * — dorobimy w T12.H.10 razem z paletą buildera. Settings.tabs definiuje
 * tytuły+ikony, ale aktywna zakładka renderuje wszystkie children łącznie.
 *
 * Settings: { tabs: [{ id, label, icon? }], defaultTabId? }
 */

import { useState } from 'react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';

interface TabConfig {
  id: string;
  label: string;
  icon?: string;
}

function TabsView({
  tabs,
  defaultTabId,
  children,
  isBuilder,
}: {
  tabs: TabConfig[];
  defaultTabId?: string;
  children: React.ReactNode[];
  isBuilder: boolean;
}) {
  const [activeId, setActiveId] = useState(defaultTabId ?? tabs[0]?.id);
  const hasTabs = tabs.length > 0;

  return (
    <div
      className={
        isBuilder ? 'ring-2 ring-transparent hover:ring-accent-200 transition rounded p-1' : ''
      }
    >
      <div className="flex gap-1 border-b border-border-primary" role="tablist">
        {hasTabs ? (
          tabs.map((tab) => {
            const isActive = tab.id === activeId;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveId(tab.id)}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-b-2 border-accent-500 text-accent-600'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {tab.label}
              </button>
            );
          })
        ) : (
          <span className="px-3 py-2 text-xs italic text-text-muted">
            (brak zakładek — dodaj w panelu właściwości)
          </span>
        )}
      </div>
      <div className="mt-3" role="tabpanel">
        {children.length > 0 ? (
          <div className="space-y-3">{children}</div>
        ) : isBuilder ? (
          <p className="text-xs italic text-text-muted">
            (zawartość zakładki pusta — przeciągnij elementy)
          </p>
        ) : null}
      </div>
    </div>
  );
}

export const TabsElement: LayoutElementDefinition = {
  type: 'tabs',
  category: 'interaktywne',
  isContainer: true,
  label: 'Zakładki',
  icon: 'PanelTop',
  defaultSettings: () => ({
    tabs: [
      { id: 'tab-1', label: 'Zakładka 1' },
      { id: 'tab-2', label: 'Zakładka 2' },
    ] as TabConfig[],
    defaultTabId: 'tab-1',
  }),
  render: ({ element, children, mode }) => {
    const settings = (element.settings ?? {}) as {
      tabs?: TabConfig[];
      defaultTabId?: string;
    };
    const tabs = settings.tabs ?? [];
    const isBuilder = mode === 'builder';

    return (
      <div data-testid={`tabs-element-${element.id}`} data-element-type="tabs">
        <TabsView
          tabs={tabs}
          defaultTabId={settings.defaultTabId}
          children={children}
          isBuilder={isBuilder}
        />
      </div>
    );
  },
};
